/**
 * 将 Create Manufacturer / Create Responsible Person 的平台错误映射到表单字段。
 * 错误码对齐 TikTok OpenAPI 202409 compliance 文档。
 */

import i18n from '@/i18n';
import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';

function complianceText(key, fallback, options) {
  const fb = fallback == null || typeof fallback !== 'string' ? fallback : fixedUi(fallback, options);
  if (!i18n.isInitialized) return fb;
  const raw = i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
  return raw == null || typeof raw !== 'string' ? raw : fixedUi(raw, options);
}

const CODE_FIELD = {
  // 共用
  38023041: 'local_number', // Invalid phone number length
  38023042: 'email', // Invalid email format
  38023043: 'address', // Invalid address length（负责人落在 street_address_line1）
  38023044: 'name', // Invalid name length
  38023046: 'country_code', // Invalid phone country code format
  38023060: 'country_code', // Invalid phone number availability
  // 制造商
  38023033: 'name', // Invalid manufacturer's name
  38023034: 'address', // Invalid manufacturer's address
  38023037: 'registered_trade_name', // Invalid registered trade name
  38023045: 'registered_trade_name', // Invalid registered trade name length
  // 负责人
  38023022: 'name', // Invalid responsible person's name
  38023024: 'address', // Invalid responsible person's address
  38023026: 'country', // Invalid EU country
  38023028: 'country_code', // Invalid EU phone country code
};

const CODE_HINT = {
  38023041: '电话号码长度无效：本地号码须为数字，制造商 7–20 位、负责人 7–11 位',
  38023042: '邮箱格式无效',
  38023043: '地址长度无效',
  38023044: '名称长度无效',
  38023046: '电话国家码格式无效（须以 + 开头，最多 4 位数字）',
  38023060: '电话可用性取值无效（AVAILABLE / UNAVAILABLE）',
  38023033: '制造商名称无效',
  38023034: '制造商地址无效',
  38023037: '注册商号无效',
  38023045: '注册商号长度无效',
  38023022: '负责人姓名无效',
  38023024: '负责人地址无效',
  38023026: '国家须为欧盟国家二字码（如 IE、DE）',
  38023028: '电话国家码须为欧盟区号（如 +353）',
  38023050: '跨境全球卖家不支持此接口，仅欧盟本土店可用',
  38023032: '制造商数量已达上限',
  38023027: '负责人数量已达上限',
};

function extractPlatformCode(error) {
  const payload = error?.payload;
  const candidates = [
    error?.platformCode,
    error?.tiktokCode,
    payload?.platformCode,
    payload?.errorCode,
    payload?.code,
  ];
  for (const raw of candidates) {
    const n = Number(String(raw || '').replace(/\D/g, ''));
    if (Number.isFinite(n) && n >= 38023000 && n < 38024000) return n;
  }
  const text = [
    error?.message,
    error?.rawMessage,
    payload?.message,
  ].map((x) => String(x || '')).join(' ');
  const hit = text.match(/38023\d{3}/);
  return hit ? Number(hit[0]) : 0;
}

function extractMessage(error) {
  const text = String(
    error?.rawMessage
    || error?.message
    || error?.payload?.message
    || '',
  ).trim();
  return text;
}

function mapByMessage(message = '', kind = 'manufacturer') {
  const msg = String(message || '').toLowerCase();
  if (/phone number length|本地号码|电话.*长度|invalid phone number length/.test(msg)) {
    return 'local_number';
  }
  if (/phone country code|eu phone|国家码|country_code|availability/.test(msg)) {
    return 'country_code';
  }
  if (/email|邮箱/.test(msg)) return 'email';
  if (/registered.?trade.?name|注册商号/.test(msg)) return 'registered_trade_name';
  if (/eu country|invalid eu country|国家/.test(msg) && kind === 'responsiblePerson') {
    return 'country';
  }
  if (/postal|邮编/.test(msg) && kind === 'responsiblePerson') return 'postal_code';
  if (/address|地址|street/.test(msg)) return 'address';
  if (/name|名称|姓名/.test(msg)) return 'name';
  if (/locale|语言/.test(msg)) return 'locale';
  return '';
}

/**
 * @param {unknown} error
 * @param {'manufacturer'|'responsiblePerson'} kind
 * @returns {{ field: string, message: string, code: number }}
 */
export function mapTiktokComplianceEntityError(error, kind = 'manufacturer') {
  const code = extractPlatformCode(error);
  const rawMessage = extractMessage(error);
  let field = CODE_FIELD[code] || '';
  if (!field) field = mapByMessage(rawMessage, kind);

  // 负责人地址是拆分字段，统一落到街道地址
  if (kind === 'responsiblePerson' && field === 'address') {
    field = 'street_address_line1';
  }

  const hint = CODE_HINT[code]
    ? complianceText('compliance.eu' + String(code).slice(-2), CODE_HINT[code])
    : '';
  const message = hint || (rawMessage
    ? rawMessage.replace(/^TikTok[：:]\s*/i, '').replace(/（平台 code=\d+）.*/i, '').trim()
    : complianceText('compliance.euCreateFail', '创建失败'));

  return {
    field: field || '',
    message: message || complianceText('compliance.euCreateFail', '创建失败'),
    code: code || 0,
  };
}

/**
 * 客户端必填校验（提交前）。
 * @returns {Record<string, string>}
 */
export function validateComplianceEntityForm(form, kind = 'manufacturer') {
  const errors = {};
  const missing = (key, label) => {
    if (!String(form?.[key] || '').trim()) {
      const name = complianceText(label, label);
      errors[key] = fixedUiTpl('请填写{0}', name);
    }
  };
  if (kind === 'manufacturer') {
    missing('name', 'compliance.euName');
    missing('email', 'compliance.euEmail');
    missing('country_code', 'compliance.euCountryCode');
    missing('local_number', 'compliance.euLocalNo');
    missing('address', 'compliance.euAddress');
  } else {
    missing('name', 'compliance.euPerson');
    missing('email', 'compliance.euEmail');
    missing('country_code', 'compliance.euCountryCode');
    missing('local_number', 'compliance.euLocalNo');
    missing('street_address_line1', 'compliance.euStreet');
    missing('postal_code', 'compliance.euPostal');
    missing('country', 'compliance.euCountry');
  }
  return errors;
}

export function focusComplianceEntityField(rootEl, field) {
  if (!rootEl || !field) return;
  const wrap = rootEl.querySelector(`[data-field-error="${field}"]`);
  if (!wrap) return;
  if (typeof wrap.scrollIntoView === 'function') {
    wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  const input = wrap.querySelector('input, textarea, select');
  if (input && typeof input.focus === 'function') {
    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
  }
}
